
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import Vuetable, { VuetablePagination, VuetablePaginationInfo } from 'vuetable-2';
import { handleError, initClient } from '../../api';
import { fixISettings, formatDateTime, formatDateTimeSeconds } from '../../util';
import ActivityModal from '../activitymodal/ActivityModal';
import { EnumStatus } from '../activitymodal/activitymodal.model';
import UserSelectModal from '../activitymodal/userselectmodal/userselectmodal.controller';
import {
  Activity,
  ActivityControllerUIClient,
  ActivityColumns,
  ActivityPriority,
  CreateFlow,
  Duty,
  ListItem,
  SettingsApiControllerUIClient,
  Cell,
  CellClient,
  ActivityDistinctValues,
  ActivitySettings,
  AskModal
} from '../backend/backendapi';
import vuetableCss from './style';
import { Operator, SearchEvent, Suggestion } from '../search/search.model';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import TriggerModal from '../actions/TriggerModal';
import ActionDefinitionStore from '../../stores/ActionDefinitionStore';
import FlowModal from './flowmodal/flowmodal.controller';
import AskModal from '../AiAsk/AskModal';
declare var window: WindowType;
Vue.use(Toasted, { duration: 2000 });

@Component({
  name: 'ActivityComponent',
  components: {
    TriggerModal,
    vSelect,
    Vuetable, // see https://www.vuetable.com/guide/
    VuetablePagination,
    VuetablePaginationInfo,
    ActivityModal,
    ColumnSelectorModal: require('./columnSelector/columnSelector.vue.html').default,
    FilterBar: require('../filterbar/filterbar.vue.html').default,
    FlowModal: require('./flowmodal/flowmodal.vue.html').default,
    Search: require('../search/search.vue.html').default,
    UserSelectModal: require('../activitymodal/userselectmodal/userselectmodal.vue.html').default,
    UserLimit: require('../userlimit/userlimit.vue.html').default,
    VuetableCustomHeader: require('./vuetableCustomHeader/vuetableCustomHeader.vue.html').default,
    AskModal: require('../AiAsk/AskModal.vue').default,
  },
})
export default class ActivityComponent extends Vue {
  //data
  private duty: Duty = new Duty();
  private activity: Activity = new Activity();
  private createFlow: CreateFlow = new CreateFlow();
  private settingsColumn: ActivityColumns | null = null;
  private settings: ActivitySettings | null = null;
  private availableResponsibles: ListItem[] = [];
  private availablePriorities: ActivityPriority[] = [];
  private readonly cells: Cell[] = [];
  private activityDistinctValues: ActivityDistinctValues | null = null;

  //api
  private readonly cellApi = initClient(CellClient);

  //runtime
  private sortOrder: any[];
  private moreparams: any = {};
  private showCheckboxes: boolean = false;
  private selectedActivities: Activity[] = [];
  private filterTimeout: number | null = null;
  private lastSearchEvent: SearchEvent<any, any, Activity> | null = null;

  readonly actionsStore = new ActionDefinitionStore();

  //
  private signalR?: HubConnection;

  //static
  private readonly fieldDefs: any[];
  private css: object;

  //api
  private readonly activityAPI = initClient(ActivityControllerUIClient);
  private readonly settingsAPI = initClient(SettingsApiControllerUIClient);

  constructor() {
    super();
    fixISettings();
    this.fieldDefs = [
      {
        ischeckbox: true,
        name: require('./vuetableFieldCheckboxFixed/vuetableFieldCheckboxFixed.vue.html').default,
        titleClass: 'center aligned',
        dataClass: 'center aligned',
      },
      {
        settingsName: 'Responsible',
        name: 'isUserResponsible',
        title: '',
        sortField: 'isUserResponsible',
        formatter: (value) => value ? '*' : '',
      },
      {
        settingsName: 'Status',
        name: 'status',
        title: '<span class="fas fa-user-check"></span> ',
        sortField: 'status',
        formatter: (value) => this.getStatus(value),
      },
      {
        settingsName: 'ChangeDate',
        name: 'currentDate',
        title: this.getDateStr(),
        sortField: 'currentDate',
        formatter: formatDateTime,
      },
      {
        settingsName: 'Activity',
        name: 'title',
        title: this.getTitleStr(),
        formatter: this.escapeTags,
      },
      {
        settingsName: 'Duty',
        name: 'duty.title',
        title: this.getDutyStr(),
        formatter: this.escapeTags,
      },
      {
        settingsName: 'Cell',
        name: 'cellName',
        title: this.getCellStr(),
        sortField: 'cellname',
        formatter: this.escapeTags,
      },
      {
        settingsName: 'CellPath',
        name: 'cellParents',
        title: this.getCellPathStr(),
        formatter: this.escapeTags,
      },
      {
        settingsName: 'Reference',
        name: 'reference',
        title: this.getReferenceStr(),
        formatter: this.escapeTags,
      },
      {
        settingsName: 'User',
        name: 'username',
        title: this.getUsernameStr(),
        sortField: 'username',
        formatter: this.escapeTags,
      },
      {
        settingsName: 'ResponsibleSet',
        name: 'fkUserResponsibleName',
        title: this.getResponsiblenameStr(),
        formatter: this.escapeTags,
      },
      {
        settingsName: 'Prio',
        name: 'priority.name',
        title: this.getPrionameStr(),
        formatter: this.escapeTags,
      },
      {
        settingsName: 'ActivityId',
        name: 'id',
        title: this.getIdStr(),
      },
      {
        settingsName: 'Techarea',
        name: 'techArea',
        title: this.getTechareanameStr(),
        formatter: this.escapeTags,
      },
      {
        settingsName: 'Actions',
        name: 'actions',
        title: this.getActionsStr(),
      }];
    this.sortOrder = [
      { field: 'name', direction: 'asc' },
    ];
    this.css = {
      table: {
        tableClass: 'table table-striped table-bordered table-hovered',
      },
    };
  }

  async mounted(): Promise<void> {
    var query = new URL(location.href).searchParams.get('Act');
    if (query) {
      this.loadAndShow(parseInt(query));
    }
    this.signalR = this.initSignalR();
    await Promise.all([
      this.reloadSettings(),
      this.reloadResponsibles(),
      this.reloadPriorities(),
      this.reloadCells(),
      this.reloadActivityDistinctValues(),
    ]);
    this.onFilterChanged();//settings.permanentSearch may have some criteria
    this.watchColumnsToShow();

    await this.actionsStore.reload();
  }

  private loadAndShow(id: number): void {
    this.activityAPI.getActivity(id)
        .then(result => {
          let activity = result.result!;
          this.activity = activity;
          this.duty = activity.duty!;
          (this.$refs.activityInfoModal as ActivityModal).open();
        })
        .catch(e => this.$toasted.error(this.$t('txtError') + ': ' + JSON.stringify(e)));
  }

  beforeDestroy(): void {
    this.signalR?.stop();
  }

  private initSignalR(): HubConnection {
    const connection = new HubConnectionBuilder()
        .withUrl('notifyHub')
        .build();
    connection.on('ActivityRefresh', (user, actid, message) => {
      this.refresh();
    });
    connection.start().catch(err => console.error(err));
    return connection;
  }

  private async reloadSettings(): Promise<void> {
    const set = (await this.settingsAPI.get('ActivityColumns', false, false)).result!;
    this.settingsColumn = new ActivityColumns();
    this.settingsColumn.init(set);

    const set2 = (await this.settingsAPI.get('ActivitySettings', false, false)).result!;
    this.settings = new ActivitySettings();
    this.settings.init(set2);
  }

  private async reloadResponsibles(): Promise<void> {
    const resp = (await this.activityAPI.getAvailableResponsibles()).result!;
    this.availableResponsibles.splice(0);
    this.availableResponsibles.push(...resp);
  }

  private async reloadPriorities(): Promise<void> {
    const prios = (await this.activityAPI.getAvailablePriorities()).result!;
    this.availablePriorities.splice(0);
    this.availablePriorities.push(...prios);
  }

  private async reloadCells(): Promise<void> {
    const cells = await this.cellApi.getCellsData();
    this.cells.splice(0);
    this.cells.push(...cells);
  }

  private async reloadActivityDistinctValues(): Promise<void> {
    this.activityDistinctValues = await this.activityAPI.getDistinctValues();
  }

  private refresh(): void {
    let vuetable = this.$refs.vuetable as Vuetable;
    vuetable.reload();
  }

  private saveSettings(): Promise<void> {
    return this.settingsAPI.update('ActivitySettings', false, false, JSON.stringify(this.settings)).then(_ => undefined);
  }

  private transformToOpenApi(data: { activities: { data: Activity[] } }) {
    data.activities.data = data.activities.data.map(e => {
      let a = new Activity();
      a.init(e);
      return a;
    });
    return data;
  }

  //events
  private onActionClicked(data: Activity): void {
    this.activity = new Activity(data); //copy
    this.duty = new Duty(data.duty); //copy
    (this.$refs.activityInfoModal as ActivityModal).open();
  }

  private onActionOpenModal(data: Activity): void {
    this.createFlow.name = data.title;
    this.createFlow.id = data.id;
    (this.$refs.flowModal as FlowModal).openModal();
  }

   private onAiOpenModal(): void {
    (this.$refs.askModal as AskModal).openModal();
  }
  private isAiEnabled(): boolean {
    return window.application.check2.isAi;
  }

  private onExport(item: Activity): void {
    let data = '';
    data += this.$t('txtActivityTitle') + ': ' + item.title + '\n';
    data += this.$t('txtDescription') + ': ' + (item.description?.length ? item.description : ('(' + this.$t('txtEmpty') + ')')) + '\n';
    data += this.$t('txtUsername') + ': ' + item.username + '\n';
    data += this.$t('txtActivityDate') + ': ' + (item.currentDate && formatDateTimeSeconds(new Date(item.currentDate))) + '\n';
    data += this.$t('txtDutyName') + ': ' + item.duty?.title + '\n';
    let checklist = item.jsonVMChecklist ? JSON.parse(item.jsonVMChecklist) : [];
    data += this.$t('txtTaskInfo') + ': ' + (checklist.length ? checklist.filter(clitem => clitem.completed).length + '/' + checklist.length : ('(' + this.$t('txtEmpty') + ')')) + '\n';
    checklist.forEach(clitem => {
      data += '  ' + (clitem.completed ? '[x]' : '[_]') + ' ' + clitem.text + '\n';
    });
    data += this.$t('txtAttachments') + ': ' + (item.file?.length ?? ('(' + this.$t('txtEmpty') + ')')) + '\n';
    if (item.file) {
      item.file.forEach(f => {
        data += '  ' + f.publicUrl + '\n';
      });
    }

    navigator.clipboard.writeText(data);
    this.$toasted.info(this.$t('txtCopiedToClipboard').toString());
  }

  private onFilterHit(data: string): void {
    if (!data?.trim().length) {
      this.moreparams = {};
      this.$toasted.info(this.$i18n.t('txtNoFilter').toString());
    } else { // set filter disable timer
      this.moreparams = { 'filter': data };
    }
    this.$nextTick(() => {
      this.refresh();
      let table = this.$refs.vuetable as Vuetable;
      table.gotoPage(table.initialPage);
    });
  }

  private onSearch(event: SearchEvent<any, any, Activity>): void {
    this.lastSearchEvent = event;
    this.onFilterChanged();
  }

  private onFilterChanged(): void {
    var moreparamsbefore = { ...this.moreparams };

    var filterMap = new Map<string, string>();
    this.permanentSearchMap.forEach((v, k) => filterMap.set(k, v));
    this.searchBoxQueryMap.forEach((v, k) => filterMap.set(k, v));
    const filter = Array.from(filterMap.values()).join('\n');
    if (filter) {
      this.moreparams = { 'filter': filter };
    } else {
      this.moreparams = {};
    }

    //check if filter changed
    if (JSON.stringify(moreparamsbefore) === JSON.stringify(this.moreparams)) {
      return;
    }

    let table = this.$refs.vuetable as Vuetable;
    table.gotoPage(table.initialPage);

    if (this.filterTimeout) {
      window.clearTimeout(this.filterTimeout);
    }
    this.filterTimeout = window.setTimeout(this.refresh, 500);
  }

  private onPaginationData(paginationData): void {
    (this.$refs.pagination as any).setPaginationData(paginationData);
    (this.$refs.paginationinfo as any).setPaginationData(paginationData);
  }

  private onChangePage(page): void {
    var vuetable = this.$refs.vuetable as Vuetable;
    vuetable.changePage(page);

    //clear selection
    vuetable.selectedTo.splice(0);
    this.selectedActivities.splice(0);
  }

  private onColumnSelected(item: string): void {
    if (!this.settingsColumn) {
      return;
    }
    this.settingsColumn.columns.push(item);
    this.settingsAPI.update('ActivityColumns', false, false, JSON.stringify(this.settingsColumn))
        .then(_ => this.$toasted.info(this.$t('txtSaved').toString()))
        .catch(e => this.$toasted.error(this.$t('txtError') + ': ' + JSON.stringify(e)));
  }

  private onColumnUnselected(item: string): void {
    if (!this.settingsColumn) {
      return;
    }
    let index = this.settingsColumn.columns.findIndex(e => e === item);
    if (index > -1) {
      this.settingsColumn.columns.splice(index, 1);
      this.settingsAPI.update('ActivityColumns', false, false, JSON.stringify(this.settingsColumn))
          .then(_ => this.$toasted.info(this.$t('txtSaved').toString()))
          .catch(e => this.$toasted.error(this.$t('txtError') + ': ' + JSON.stringify(e)));
    } else {
      console.error('onColumnUnselected: cannot find', item, 'in', this.settingsColumn.columns);
    }
  }

  private async onResponsibleChanged(user: ListItem | null): Promise<void> {
    (this.$refs.userselectmodalmassselect as UserSelectModal).closeModal();

    let success = 0;
    let activities = [...this.selectedActivities];
    let promises: Promise<any>[] = [];
    for (let act of activities) {
      act.fkUserIdResponsible = user?.id;
      act.fkUserResponsibleName = user?.userName;
      promises.push(this.activityAPI.updateResponsible(act.id!, act.fkUserIdResponsible)
          .then(e => success++)
          .catch(e => handleError(this.$toasted, this.$i18n, e)));
    }
    let x = await Promise.all(promises);
    this.$toasted.info(this.$t('txtSaved') + ' (' + success + '/' + activities.length + ')');

    //clear selection
    this.selectedActivities.splice(0);
    let table = this.$refs.vuetable as Vuetable;
    (table.selectedTo as number[]).splice(0);
  }

  private async onMassClose(): Promise<void> {
    let success = 0;
    let activities = [...this.selectedActivities];
    let promises: Promise<any>[] = [];
    for (let act of activities) {
      act.status = EnumStatus.Done;
      promises.push(this.activityAPI.updateActivity(act)
          .then(e => success++)
          .catch(e => handleError(this.$toasted, this.$i18n, e)));
    }
    let x = await Promise.all(promises);
    this.$toasted.info(this.$t('txtClosed') + ' (' + success + '/' + activities.length + ')');

    //clear selection
    this.selectedActivities.splice(0);
    let table = this.$refs.vuetable as Vuetable;
    (table.selectedTo as number[]).splice(0);
  }

  private onSelectionChanged(isChecked: boolean, dataItem: any): void {
    let table = this.$refs.vuetable as Vuetable;
    let ids = new Set(table.selectedTo as number[]);
    this.selectedActivities.splice(0);
    this.selectedActivities.push(...(table.tableData as Activity[]).filter(e => ids.has(e.id!)));
  }

  //getters
  private getActionsStr(): string {
    var str = this.$i18n.t('activityActions').toString();
    return str;
  }

  private getDateStr(): string {
    var str = this.$i18n.t('txtActivityDate').toString();
    return '<i class="fas fa-history"></i> ' + str;
  }

  private getTitleStr(): string {
    var str = this.$i18n.t('txtActivityTitle').toString();
    return '<i class="fas fa-check-circle"></i> ' + str;
  }

  private getDutyStr(): string {
    var str = this.$i18n.t('txtDutyName').toString();
    return '<i class="fas fa-tasks"></i> ' + str;
  }

  private getCellStr(): string {
    var str = this.$i18n.t('activityCellName').toString();
    return '<i class="fas fa-hdd"></i> ' + str;
  }

  private getCellPathStr(): string {
    var str = this.$i18n.t('activityCellPath').toString();
    return '<i class="fas fa-hdd"></i> ' + str;
  }

  private getPrionameStr(): string {
    var str = this.$i18n.t('activityPrio').toString();
    return '<i class="fas fa-exclamation"></i> ' + str;
  }

  private getReferenceStr(): string {
    var str = this.$i18n.t('txtActivityReference').toString();
    return '<i class="fas fa-asterisk"></i> ' + str;
  }

  private getResponsiblenameStr(): string {
    var str = this.$i18n.t('txtResponsibles').toString();
    return '<i class="fas fa-user-lock"></i> ' + str;
  }

  private getTechareanameStr(): string {
    var str = this.$i18n.t('activityTecharea').toString();
    return '<i class="fas fa-map-marker-alt"></i> ' + str;
  }

  private getUsernameStr(): string {
    var str = this.$i18n.t('txtUsername').toString();
    return '<i class="fas fa-user"></i> ' + str;
  }

      private getIdStr(): string {
      var str = this.$i18n.t('acsActivityId').toString();
      return '<i class="fas fa-tag"></i> ' + str;
    }

  private get mycss() {
    return vuetableCss;
  }

  private get fields(): any[] {
    if (!this.settingsColumn) {
      return this.fieldDefs;
    }
    let colBySetting = new Map<string, object>();
    this.fieldDefs.forEach(v => colBySetting.set(v.settingsName, v));

    let ret: any[] = [];

    //add selected cols in order
    this.settingsColumn?.columns.forEach(sc => {
      let col = colBySetting.get(sc);
      if (!col) {
        return;
      }
      ret.push(col);
    });

    //add non selected cols
    this.fieldDefs.filter(e => !ret.includes(e) && e.settingsName !== 'Actions' && !e.ischeckbox).forEach(e => ret.push(e));

    //add actions col
    ret.push(colBySetting.get('Actions'));

    //Add checkbox col
    ret = [this.fieldDefs.find(e => e.ischeckbox)].concat(ret);

    return ret;
  }

  private get selectedFields(): string[] {
    return this.settingsColumn?.columns.filter(e => e !== 'Actions') ?? [];
  }

  private get availableFields(): string[] {
    return this.fieldDefs.filter(e => e.settingsName !== 'Actions' && !e.ischeckbox).map(e => e.settingsName);
  }

  private get selectedResponsible(): ListItem | null {
    if (this.selectedActivities.length === 0) {
      return null;
    }
    let userId = this.selectedActivities[0].fkUserIdResponsible;
    if (!this.selectedActivities.every(e => e.fkUserIdResponsible === userId)) {
      return null;
    }

    return this.availableResponsibles.find(e => e.id === userId) ?? null;
  }

  private get suggestions(): Suggestion<any, any, Activity>[] {
    return [
      new Suggestion('cell', this.$t('activityCellName').toString(), [
        new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
      ], this.distinctCells, (c: Activity) => c.cellName ?? ''),
      new Suggestion('reference', this.$t('txtActivityReference').toString(), [
        new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
      ], this.distinctReferences, (c: Activity) => c.reference ?? ''),
      new Suggestion('techarea', this.$t('activityTecharea').toString(), [
        new Operator('eq', '=', (ex: string, ac: string) => ex.toLocaleLowerCase().includes(ac.toLocaleLowerCase())),
      ], this.distinctTechAreas, (c: Activity) => c.techArea ?? ''),
    ].sort((l, r) => l.display.localeCompare(r.display));
  }

  private get distinctCells(): string [] {
    let ret = this.cells.map(e => e.name!).sort((l, r) => l.toLocaleLowerCase().localeCompare(r.toLocaleLowerCase()));
    ret = ['NA', ...ret];
    return ret;
  }

  private get distinctReferences(): string [] {
    return this.activityDistinctValues?.references.sort() ?? [];
  }

  private get distinctResponsibles(): string [] {
    return this.availableResponsibles.map(e => e.userName!).sort() ?? [];
  }

  private get distinctTechAreas(): string [] {
    return this.activityDistinctValues?.techAreas.sort() ?? [];
  }

  private get permanentSearchReference(): string | undefined {
    return this.settings?.permanentSearch?.reference;
  }

  private set permanentSearchReference(reference: string | undefined) {
    if (!this.settings) {
      return;
    }
    this.settings.permanentSearch.reference = reference;
    this.saveSettings();
    this.onFilterChanged();
  }

  private get permanentSearchResponsibleUserId(): string | undefined {
    return this.settings?.permanentSearch?.responsibleUserId;
  }

  private set permanentSearchResponsibleUserId(responsibleUserId: string | undefined) {
    if (!this.settings) {
      return;
    }
    this.settings.permanentSearch.responsibleUserId = responsibleUserId;
    this.saveSettings();
    this.onFilterChanged();
  }

  private get permanentSearchResponsibleUserName(): string | undefined {
    const userId = this.permanentSearchResponsibleUserId;
    return userId && this.availableResponsibles.find(e => e.id === userId)?.userName;
  }

  private set permanentSearchResponsibleUserName(responsibleUserName: string | undefined) {
    const userId = this.availableResponsibles.find(e => e.userName === responsibleUserName)?.id;
    this.permanentSearchResponsibleUserId = userId;
  }

  private get permanentSearchTechArea(): string | undefined {
    return this.settings?.permanentSearch?.techArea;
  }

  private set permanentSearchTechArea(techArea: string | undefined) {
    if (!this.settings) {
      return;
    }
    this.settings.permanentSearch.techArea = techArea;
    this.saveSettings();
    this.onFilterChanged();
  }

  private get hiddenColumnWithActivePermanentSearch(): { name: string, value: string }[] {
    const ret: { name: string, value: string }[] = [];
    if (!this.settingsColumn?.columns.includes('Reference') && this.permanentSearchReference) {
      ret.push({ name: this.$t('txtActivityReference').toString(), value: this.permanentSearchReference });
    }
    if (!this.settingsColumn?.columns.includes('ResponsibleSet') && this.permanentSearchResponsibleUserName) {
      ret.push({ name: this.$t('txtResponsibles').toString(), value: this.permanentSearchResponsibleUserName });
    }
    if (!this.settingsColumn?.columns.includes('Techarea') && this.permanentSearchTechArea) {
      ret.push({ name: this.$t('activityTecharea').toString(), value: this.permanentSearchTechArea });
    }
    return ret.sort((l, r) => l.name.localeCompare(r.name));
  }

  private get hiddenColumnWithActivePermanentSearchString(): string {
    return this.hiddenColumnWithActivePermanentSearch.map(e => ' - ' + e.name + ': ' + e.value).join('\n');
  }

  private get searchBoxQueryMap(): Map<string, string> {
    const map = new Map<string, string>();
    this.lastSearchEvent?.segments
        .filter(s => s.operator && s.value.trim().length)
        .forEach(s => map.set(s.suggestion.key, s.suggestion.key + s.operator?.display + s.value));
    if (this.lastSearchEvent?.search.trim().length) {
      map.set('search', 'search=' + this.lastSearchEvent?.search.trim());
    }
    return map;
  }

  private get permanentSearchMap(): Map<string, string> {
    const map = new Map<string, string>();
    if (this.settings?.permanentSearch.reference) {
      map.set('reference', 'reference=' + this.settings.permanentSearch.reference);
    }
    if (this.settings?.permanentSearch.responsibleUserId) {
      map.set('responsible', 'responsible=' + this.settings.permanentSearch.responsibleUserId);
    }
    if (this.settings?.permanentSearch.techArea) {
      map.set('techarea', 'techarea=' + this.settings.permanentSearch.techArea);
    }
    return map;
  }

  // watchers
  @Watch('settingsColumn', { deep: true })
  @Watch('showCheckboxes')
  private watchColumnsToShow(): void {
    // needs to be delayed when settingsColumn.columns changes because the fields for the vue table are recomputed 
    // and vuetable needs 1 tick to be ready to receive calls to hide/show columns
    this.$nextTick(() => {
      let table = this.$refs.vuetable as Vuetable | undefined;
      if (!table) {
        return;
      }
      let fn = { 'true': table.showField, 'false': table.hideField };
      this.fields.forEach((v, i) => {
        if (v.ischeckbox) {
          fn['' + this.showCheckboxes](i);
        } else {
          let show = v.settingsName === 'Actions' || this.settingsColumn === null || this.settingsColumn.columns.includes(v.settingsName);
          fn['' + show](i);
        }
        return v;
      });
    });
  }

  @Watch('showCheckboxes')
  private watchClearSelection() {
    let table = this.$refs.vuetable as Vuetable;
    table.selectedTo.splice(0);
    this.selectedActivities.splice(0);
  }

  //mapper
  private XXgetStatus(value: EnumStatus): string {
    switch (value) {
      case EnumStatus.Triggered:
      case EnumStatus.Changed:
      case EnumStatus.Postponed:
      case EnumStatus.SignedOff:
      case EnumStatus.Document:
      case EnumStatus.Done:
        var str = this.getStatusString(value);
        return '<i class="fas fa-check"></i> ' + str;
      default: {
        return 'ND';
      }
    }
  }

  private getStatus(value: EnumStatus): string {
  const text = this.getStatusString(value);
  let icon = 'fa-check';
  let statusClass = 'status-default';

  switch (value) {
    case EnumStatus.Changed:
      icon = 'fa-pen-nib';
      statusClass = 'status-changed'; // Blue/Information
      break;
    case EnumStatus.SignedOff:
      icon = 'fa-file-signature';
      statusClass = 'status-signed'; // Success/Green
      break;
    case EnumStatus.Postponed:
      icon = 'fa-clock';
      statusClass = 'status-postponed'; // Warning/Orange
      break;
    case EnumStatus.Document:
      icon = 'fa-file-alt';
      statusClass = 'status-document'; // Neutral/Indigo
      break;
    case EnumStatus.Triggered:
    case EnumStatus.Done:
      icon = 'fa-check-circle';
      statusClass = 'status-done';
      break;
    default:
      return '<span class="badge badge-light">ND</span>';
  }

  return `<span class="status-pill ${statusClass}"><i class="fas ${icon}"></i> ${text}</span>`;
}

  private getStatusString(value): string {
    switch (value) {
      case EnumStatus.Triggered: {
        return this.$i18n.t('txtTriggered').toString();
      }
      case EnumStatus.Changed: {
        return this.$i18n.t('txtChanged').toString();
      }
      case EnumStatus.Postponed: {
        return this.$i18n.t('txtPostponed').toString();
      }
      case EnumStatus.SignedOff: {
        return this.$i18n.t('txtSignedOff').toString();
      }
      case EnumStatus.Document: {
        return this.$i18n.t('txtDocument').toString();
      }
      case EnumStatus.Done: {
        return this.$i18n.t('txtDone').toString();
      }
      default: {
        return 'ND';
      }
    }
  }

  private escapeTags(value: string | null | undefined): string | null | undefined {
    let before = value;
    do {
      before = value;
      value = value?.replace('<', '&lt;').replace('>', '&gt;');
    } while (before !== value);
    return value;
  }
}
